import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, ExternalLink, Handshake } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { withLang } from '../components/LanguageRouter';

const PartnersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const partners = [
    {
      id: 'valsol',
      name: 'VALSOL Trading SpA',
      tagline: t('partners.valsol.tagline'),
      description: t('partners.valsol.description'),
      website: 'https://valsoltrading.cl',
      websiteLabel: 'valsoltrading.cl',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="partners-page">
      <SEO
        title="Our International Partners - Xiamen Tower Technology"
        description="Meet the official international distribution partners of Xiamen Tower Technology Co., Ltd. Trusted collaborators delivering reliable sourcing and import solutions from China worldwide."
        keywords="xiamen tower technology partners, valsol trading chile, china sourcing partners, international distribution, import export partners"
      />
      <Header />

      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Navigation Bar */}
          <div className="mb-8 flex items-center justify-between max-w-5xl mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
              data-testid="partners-back-button"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t('legal.goBack')}
            </button>
            <Link
              to={withLang(currentLanguage)}
              className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200"
              data-testid="partners-home-link"
            >
              <Home className="w-5 h-5 mr-2" />
              {t('footer.home')}
            </Link>
          </div>

          {/* Page Header */}
          <div className="max-w-5xl mx-auto text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg">
                <Handshake className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1
              className="text-4xl sm:text-5xl font-bold text-blue-900 mb-4"
              data-testid="partners-page-title"
            >
              {t('partners.title')}
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('partners.subtitle')}
            </p>
          </div>

          {/* Partners Grid */}
          <div className="max-w-5xl mx-auto grid md:grid-cols-1 gap-8">
            {partners.map((partner) => (
              <article
                key={partner.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-100 overflow-hidden"
                data-testid={`partner-card-${partner.id}`}
              >
                <div className="p-8 md:p-10">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                    <div className="flex-1">
                      <h2
                        className="text-2xl md:text-3xl font-bold text-blue-900 mb-2"
                        data-testid={`partner-name-${partner.id}`}
                      >
                        {partner.name}
                      </h2>
                      <p
                        className="text-base font-semibold text-blue-600 mb-4"
                        data-testid={`partner-tagline-${partner.id}`}
                      >
                        {partner.tagline}
                      </p>
                      <p
                        className="text-gray-700 leading-relaxed whitespace-pre-line mb-6"
                        data-testid={`partner-description-${partner.id}`}
                      >
                        {partner.description}
                      </p>

                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold text-gray-800">
                          {t('partners.websiteLabel')}:
                        </span>
                        <a
                          href={partner.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors"
                          data-testid={`partner-website-${partner.id}`}
                        >
                          {partner.websiteLabel}
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Footer CTA */}
          <div className="max-w-3xl mx-auto mt-16 text-center">
            <p className="text-gray-600 mb-4">{t('partners.becomePartner')}</p>
            <button
              onClick={() => {
                navigate(withLang(currentLanguage));
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md"
              data-testid="partners-contact-cta"
            >
              {t('partners.contactCta')}
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PartnersPage;
